import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Bell, X, ChevronRight, Clock, AlertTriangle, Info, XCircle, ThumbsDown, Zap, MessageCircle, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  critical: { bg: "bg-red-50 border-red-200", icon: "text-red-500 bg-red-100", dot: "bg-red-500" },
  warning: { bg: "bg-amber-50 border-amber-200", icon: "text-amber-600 bg-amber-100", dot: "bg-amber-500" },
  info: { bg: "bg-sky-50 border-sky-200", icon: "text-sky-600 bg-sky-100", dot: "bg-sky-500" },
};

const TYPE_LABELS = {
  stale_deal: "Stale Deal",
  lender_interest_no_ts: "Lender Follow-up",
  lender_nudge: "Deal Nudge",
  borrower_missing_docs: "Missing Docs",
  borrower_no_activity: "Needs Attention",
};

const NOTIF_ICONS = {
  message: MessageCircle,
  deal_update: FileText,
  stage_change: Zap,
};

export default function ProactiveAlertPanel() {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("alerts");
  const [alerts, setAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchData = useCallback(() => {
    axios.get(`${API}/alerts/my`, { headers }).then(r => {
      setAlerts(r.data.alerts || []);
      setAlertCount(r.data.unread_count || 0);
    }).catch(() => {});
    axios.get(`${API}/notifications`, { headers }).then(r => {
      const notifs = r.data?.notifications || r.data || [];
      setNotifications(Array.isArray(notifs) ? notifs.slice(0, 20) : []);
    }).catch(() => {});
    axios.get(`${API}/notifications/unread-count`, { headers }).then(r => {
      setNotifCount(r.data?.count || r.data?.unread || 0);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
      const interval = setInterval(fetchData, 60000);
      return () => clearInterval(interval);
    }
  }, [token, fetchData]);

  const totalCount = alertCount + notifCount;

  const dismissAlert = (alertId, reason = null) => {
    axios.post(`${API}/alerts/${alertId}/dismiss`, { reason }, { headers }).then(() => {
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      setAlertCount(prev => Math.max(0, prev - 1));
    }).catch(() => {});
  };

  const actionAlert = (alert) => {
    axios.post(`${API}/alerts/${alert.alert_id}/action`, {}, { headers }).then(() => {
      setAlerts(prev => prev.filter(a => a.alert_id !== alert.alert_id));
      setAlertCount(prev => Math.max(0, prev - 1));
    }).catch(() => {});
    if (alert.lead_id) navigate(`/deal360/${alert.lead_id}`);
    setOpen(false);
  };

  const markNotifRead = (notifId) => {
    axios.post(`${API}/notifications/${notifId}/read`, {}, { headers }).then(() => {
      setNotifications(prev => prev.map(n => (n.notification_id || n.notif_id) === notifId ? { ...n, is_read: true } : n));
      setNotifCount(prev => Math.max(0, prev - 1));
    }).catch(() => {});
  };

  const markAllRead = () => {
    axios.post(`${API}/notifications/mark-all-read`, {}, { headers }).then(() => {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setNotifCount(0);
    }).catch(() => {});
  };

  if (!token) return null;

  return (
    <div ref={ref} className="relative" data-testid="notification-center">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-white/10 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="h-4.5 w-4.5 text-cream/70" />
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-red-500 text-[8px] font-bold text-white animate-pulse" data-testid="notification-badge-count">
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[400px] max-h-[520px] rounded-2xl border border-[#EDE8DC] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)] overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-950 to-emerald-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-[#E5C98B]" />
              <span className="text-sm font-bold text-white">Notifications</span>
              {totalCount > 0 && <span className="text-[10px] font-bold text-[#E5C98B] bg-white/10 px-2 py-0.5 rounded-full">{totalCount} new</span>}
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-3 py-2 bg-[#FDFCF9] border-b border-[#EDE8DC]">
            {[
              { key: "alerts", label: "Alerts", count: alertCount },
              { key: "notifications", label: "Activity", count: notifCount },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`notif-tab-${t.key}`}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${tab === t.key ? "bg-emerald-700 text-white" : "text-[#888] hover:text-[#555] hover:bg-[#f0ece4]"}`}>
                {t.label}
                {t.count > 0 && <span className={`px-1.5 rounded text-[8px] font-bold ${tab === t.key ? "bg-white/20" : "bg-red-100 text-red-600"}`}>{t.count}</span>}
              </button>
            ))}
            {tab === "notifications" && notifCount > 0 && (
              <button onClick={markAllRead} className="ml-auto text-[10px] font-medium text-[#999] hover:text-emerald-700" data-testid="mark-all-read-btn">Mark all read</button>
            )}
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[400px]">
            {tab === "alerts" && (
              alerts.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <Zap className="h-8 w-8 text-[#ddd] mx-auto mb-2" />
                  <p className="text-sm text-[#999]">No active alerts</p>
                  <p className="text-[10px] text-[#bbb] mt-1">Alerts appear when deals need attention</p>
                </div>
              ) : (
                alerts.map(alert => {
                  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
                  const isLenderNudge = alert.alert_type === "lender_nudge";
                  return (
                    <div key={alert.alert_id} className={`mx-2 mt-2 rounded-xl border p-3 ${style.bg}`} data-testid={`alert-item-${alert.alert_id}`}>
                      <div className="flex items-start gap-2.5">
                        <div className={`p-1.5 rounded-lg ${style.icon} shrink-0`}>
                          {alert.severity === "critical" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-[#999]">{TYPE_LABELS[alert.alert_type] || alert.alert_type}</span>
                            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                          </div>
                          <p className="text-xs font-bold text-[#0A0A0A] mb-0.5">{alert.title}</p>
                          <p className="text-[10px] text-[#666] leading-relaxed">{alert.message}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Clock className="h-2.5 w-2.5 text-[#bbb]" />
                            <span className="text-[9px] text-[#bbb]">{alert.created_at ? new Date(alert.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2.5 pt-2 border-t border-black/5">
                        <button onClick={() => actionAlert(alert)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-700 text-white hover:bg-emerald-800 transition-colors" data-testid={`alert-view-${alert.alert_id}`}>
                          View Deal <ChevronRight className="h-3 w-3" />
                        </button>
                        {isLenderNudge && (
                          <button onClick={() => dismissAlert(alert.alert_id, "not_interested")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors" data-testid={`alert-not-interested-${alert.alert_id}`}>
                            <ThumbsDown className="h-3 w-3" /> Not Interested
                          </button>
                        )}
                        <button onClick={() => dismissAlert(alert.alert_id)} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-medium text-[#999] hover:text-[#555] hover:bg-black/5 transition-colors ml-auto" data-testid={`alert-dismiss-${alert.alert_id}`}>
                          <XCircle className="h-3 w-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })
              )
            )}

            {tab === "notifications" && (
              notifications.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <Bell className="h-8 w-8 text-[#ddd] mx-auto mb-2" />
                  <p className="text-sm text-[#999]">No notifications</p>
                </div>
              ) : (
                notifications.map(n => {
                  const nid = n.notification_id || n.notif_id;
                  const Icon = NOTIF_ICONS[n.type] || Bell;
                  const isUnread = !n.is_read;
                  return (
                    <div key={nid}
                      onClick={() => { if (isUnread) markNotifRead(nid); if (n.link) { navigate(n.link); setOpen(false); } }}
                      className={`mx-2 mt-2 rounded-xl border p-3 cursor-pointer transition-colors ${isUnread ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-[#EDE8DC] opacity-70"}`}
                      data-testid={`notif-item-${nid}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`p-1.5 rounded-lg shrink-0 ${isUnread ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold ${isUnread ? "text-[#0A0A0A]" : "text-[#888]"}`}>{n.title || "Notification"}</p>
                          <p className="text-[10px] text-[#666] leading-relaxed mt-0.5">{n.body || n.message || ""}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Clock className="h-2.5 w-2.5 text-[#bbb]" />
                            <span className="text-[9px] text-[#bbb]">{n.created_at ? new Date(n.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          </div>
                        </div>
                        {isUnread && <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0 mt-1" />}
                      </div>
                    </div>
                  );
                })
              )
            )}
            <div className="h-2" />
          </div>
        </div>
      )}
    </div>
  );
}
